import type { User } from '@mend/types/user';

import * as React from 'react';
import { useQuery } from '@tanstack/react-query';

import { UsersAutocomplete } from '#components/Autocomplete/Users/UsersAutocomplete.tsx';
import { getUsers } from '#services/api/user.ts';
import { useFilterAutocomplete } from './use-filter-autocomplete.ts';

const defaultProps = {
  inputLabel: 'Provider',
  inputSize: 'medium',
} as const;

export function Provider(props: { defaultValue: User['id'][] }) {
  // Cache the initial user ids because we only need them in the first render
  // to determine if we need to enable the one-off query or not.
  const [defaultIds] = React.useState(() => props.defaultValue);

  // Fetch the list of users here instead of the loader because we only need it
  // the first time the component is rendered in order to populate the chips.
  const query = useQuery({
    queryKey: ['filters', 'providers', defaultIds],
    queryFn: () => getUsers({ id: defaultIds, role: ['Provider', 'Provider Admin'] }),
    staleTime: Infinity,
    enabled: defaultIds.length > 0,
  });

  const filterAutocomplete = useFilterAutocomplete({
    searchParamKey: 'pId',
    defaultValue: defaultIds,
    data: query.data?.users,
  });

  if (query.isLoading) {
    return <UsersAutocomplete {...defaultProps} disabled value={null} />;
  }

  return (
    <UsersAutocomplete
      getUsersParams={() => ({ role: ['Provider', 'Provider Admin'] })}
      {...defaultProps}
      {...filterAutocomplete.getAutocompleteProps()}
    />
  );
}
